/**
 * useClarityPageReady - Signals Clarity that the page UI is fully rendered
 * 
 * Prevents Clarity from capturing white/unstyled screens by waiting for:
 * 1. Double requestAnimationFrame (ensures DOM is painted)
 * 2. 800ms fallback (for slow devices)
 * 
 * Usage: Call this hook in every step page component
 */
import { useEffect } from 'react';

const useClarityPageReady = (pageName = 'unknown') => {
  useEffect(() => {
    const firePageReady = () => {
      if (typeof window.clarity === 'function') {
        try {
          window.clarity('event', 'page_ready');
          window.clarity('set', 'render_state', 'ready');
          window.clarity('set', 'current_step', pageName);
          
          // Debug logging
          if (process.env.NODE_ENV === 'development' || 
              window.location.search.includes('debugClarity=1')) {
            console.log(`[Clarity] page_ready fired for: ${pageName}`);
          }
        } catch (e) {
          console.warn('[Clarity] Failed to fire page_ready:', e);
        }
      }
    };

    // Double requestAnimationFrame ensures DOM is fully painted
    // First rAF schedules for next frame, second ensures paint is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(firePageReady);
    });

    // Fallback for slow devices - fire again after 800ms
    const fallbackTimer = setTimeout(firePageReady, 800);

    return () => clearTimeout(fallbackTimer);
  }, [pageName]);
};

export default useClarityPageReady;
